import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeEngagement } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayeeEngagements } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import React from "react";

export interface ArchiveEngagementModalProps {
  payeeId: string;
  payeeName: string;
  engagement: PayeeEngagement;
  onClose: () => void;
}

export const ArchiveEngagementModal: React.FC<ArchiveEngagementModalProps> = ({
  payeeId,
  payeeName,
  engagement,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  // v3 removed `status` from the update body: lifecycle moves through action verbs
  // (`.claude/rules/status-machines.md` — Created → Activated → Suspended → Terminated).
  // Archive maps to `suspend`, not `terminate`: terminal states cannot transition, so
  // `terminate` would strand the restore flow in `useRestorationEngagementModal`.
  const [suspendEngagement, suspendEngagementMeta] =
    queriesPayeeEngagements.suspendPayeeEngagement.useMutation();

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to archive this assignment?
      </WSText.ParagraphSm>
      <WSPanel>
        <WSFlexBox gap="XL">
          <FieldView
            label="Assignment name"
            value={engagement.engagementName}
          />
          <FieldView label="Contractor name" value={payeeName} />
        </WSFlexBox>
      </WSPanel>

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving an assignment - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            No new invoices and payments can be created for this assignment.
          </li>
          <li>Deposited invoices can be accessed on the invoicing page.</li>
        </WSElement>
        You can always restore the assignment when needed.
      </WSAlert>

      <ErrorAlert
        error={suspendEngagementMeta.error}
        mb="XL"
        context="ArchiveClient"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive assignment",
            loading: suspendEngagementMeta.isLoading,
            onAsyncClick: async () => {
              await suspendEngagement(
                {
                  payeeId,
                  payeeEngagementId: engagement.id as string
                },
                {
                  onSuccess: () => {
                    openSnackbar({
                      theme: "dark",
                      type: "success",
                      message: "Assignment archived"
                    });
                    onClose();
                  }
                }
              );
            }
          }
        ]}
      />
    </>
  );
};

export const useArchiveEngagementModal = () => {
  return useWSModal(ArchiveEngagementModal, {
    title: "Archive assignment",
    size: "M"
  });
};
